import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AccessibleChart from './AccessibleChart';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, DollarSign, CheckCircle, Users, AlertTriangle, 
  Download, Calendar, Filter 
} from 'lucide-react';

import { BACKEND_URL } from '../config';
const API = BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const AnalyticsDashboard = ({ projectId, user }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all'); // all, month, quarter, year

  useEffect(() => {
    fetchAnalytics();
  }, [projectId, dateRange]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/projects/${projectId}/analytics/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Defensive: coerce arrays inside the dashboard payload so any
      // missing/null field can't crash the chart renderers.
      const data = response.data || {};
      ['kpi_progress', 'beneficiary_demographics', 'risk_matrix', 'expense_trend', 'milestone_progress'].forEach((k) => {
        if (!Array.isArray(data[k])) data[k] = [];
      });
      data.project_overview = data.project_overview || { name: 'Unknown', progress: 0, status: 'unknown' };
      data.budget_utilization = data.budget_utilization || { allocated: 0, spent: 0, remaining: 0, utilization_pct: 0 };
      data.task_completion = data.task_completion || { completed: 0, in_progress: 0, pending: 0, total: 0 };
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const exportChartAsImage = (chartId) => {
    // Simple implementation - can be enhanced
    toast.success('Chart export feature coming soon!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600 mt-1">{analytics.project_overview.name}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Project Progress</p>
                <p className="text-3xl font-bold text-blue-600">
                  {analytics.project_overview.progress}%
                </p>
              </div>
              <TrendingUp className="h-10 w-10 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Budget Utilization</p>
                <p className="text-3xl font-bold text-green-700">
                  {analytics.budget_utilization.utilization_pct}%
                </p>
              </div>
              <DollarSign className="h-10 w-10 text-green-700 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tasks Completed</p>
                <p className="text-3xl font-bold text-purple-600">
                  {analytics.task_completion.completed}/{analytics.task_completion.total}
                </p>
              </div>
              <CheckCircle className="h-10 w-10 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Beneficiaries</p>
                <p className="text-3xl font-bold text-orange-700">
                  {analytics.beneficiary_demographics.reduce((sum, d) => sum + d.value, 0)}
                </p>
              </div>
              <Users className="h-10 w-10 text-orange-700 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* KPI Progress Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>KPI Performance</span>
              <Button variant="ghost" size="sm" onClick={() => exportChartAsImage('kpi-chart')} aria-label="Download KPI chart">
                <Download className="h-4 w-4" aria-hidden="true" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AccessibleChart
              title="KPI Performance"
              description={`Bar chart comparing baseline, current and target values across ${(analytics.kpi_progress || []).length} key performance indicators.`}
              tableData={analytics.kpi_progress || []}
              tableColumns={[
                { key: 'name', label: 'KPI' },
                { key: 'baseline', label: 'Baseline' },
                { key: 'current', label: 'Current' },
                { key: 'target', label: 'Target' },
                { key: 'progress', label: 'Progress %', format: (v) => `${Math.round(v || 0)}%` },
              ]}
              hideToggle
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.kpi_progress}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="baseline" fill="#94a3b8" name="Baseline" />
                  <Bar dataKey="current" fill="#3b82f6" name="Current" />
                  <Bar dataKey="target" fill="#10b981" name="Target" />
                </BarChart>
              </ResponsiveContainer>
            </AccessibleChart>
          </CardContent>
        </Card>

        {/* Budget Utilization */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Budget Utilization</span>
              <Button variant="ghost" size="sm" aria-label="Download chart">
                <Download className="h-4 w-4" aria-hidden="true" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AccessibleChart
              title="Budget Utilization"
              description={`Pie chart showing spent vs remaining budget. Spent: €${(analytics.budget_utilization.spent || 0).toLocaleString('el-GR')}. Remaining: €${(analytics.budget_utilization.remaining || 0).toLocaleString('el-GR')}.`}
              tableData={[
                { name: 'Spent', value: analytics.budget_utilization.spent },
                { name: 'Remaining', value: analytics.budget_utilization.remaining },
              ]}
              tableColumns={[
                { key: 'name', label: 'Segment' },
                { key: 'value', label: 'EUR', format: (v) => `€${(v || 0).toLocaleString('el-GR')}` },
              ]}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Spent', value: analytics.budget_utilization.spent },
                      { name: 'Remaining', value: analytics.budget_utilization.remaining }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#dc2626" />
                    <Cell fill="#047857" />
                  </Pie>
                  <Tooltip formatter={(value) => `€${value.toLocaleString('el-GR')}`} />
                </PieChart>
              </ResponsiveContainer>
            </AccessibleChart>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                €{analytics.budget_utilization.spent.toLocaleString('el-GR')} of €{analytics.budget_utilization.allocated.toLocaleString('el-GR')} spent
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Task Completion Status */}
        <Card>
          <CardHeader>
            <CardTitle>Task Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Completed', value: analytics.task_completion.completed },
                    { name: 'In Progress', value: analytics.task_completion.in_progress },
                    { name: 'Pending', value: analytics.task_completion.pending }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#3b82f6" />
                  <Cell fill="#94a3b8" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Beneficiary Demographics */}
        {analytics.beneficiary_demographics.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Beneficiary Demographics</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.beneficiary_demographics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#f59e0b" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Expense Trend */}
        {analytics.expense_trend.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Monthly Expense Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analytics.expense_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `€${value.toLocaleString('el-GR')}`} />
                  <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} name="Expenses" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Risk Matrix */}
        {analytics.risk_matrix.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Risk Matrix</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid />
                  <XAxis 
                    type="number" 
                    dataKey="probability" 
                    name="Probability" 
                    domain={[0, 10]}
                    label={{ value: 'Probability', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="impact" 
                    name="Impact" 
                    domain={[0, 10]}
                    label={{ value: 'Impact', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 border border-gray-200 rounded shadow">
                            <p className="font-semibold">{data.title}</p>
                            <p className="text-sm">Probability: {data.probability}</p>
                            <p className="text-sm">Impact: {data.impact}</p>
                            <p className="text-sm capitalize">Level: {data.level}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter 
                    name="Risks" 
                    data={analytics.risk_matrix} 
                    fill="#ef4444"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
